// Cloud sync layer for the points wallet + top-5 disc-count record.
//
// The game loop keeps using the synchronous localStorage wallet (points.ts) as
// its source of truth during play. This module mirrors that wallet to
// Firestore for the signed-in user so the balance follows them across devices:
//
//   - On login   → reconcile: an existing cloud balance WINS (pulled into local);
//                  if the user has no cloud doc yet, their current local balance
//                  is migrated up. Cloud-authoritative-on-login deliberately
//                  prevents "reinstall for another 500P" farming.
//   - Afterwards → the same listener keeps mirroring remote edits down (another
//                  device), and local writes are debounced and pushed up.
//
// All Firestore calls fail soft: if the network/SDK is unavailable the local
// wallet keeps working exactly as before.
//
// ⚠️ users/{uid} is SHARED with the other Gamzone games (2026, Coin Exchanger —
// same Firebase project new-2026-8eb33). The wallet field `points` is
// deliberately common to all of them; this game's own record therefore goes in
// its own `othellosScores` field (a top-5 list, matching points.ts's local
// getScores/setScores) — never reuse `galagaHi` or `scores` here.

import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "./auth/firebase.ts";
import {
  getPoints, setPoints, onPointsChange,
  getScores, setScores, onScoresChange,
  setSyncActive, getUnsyncedEarn, clearUnsyncedEarn,
} from "./points.ts";

let detachDoc: (() => void) | null = null;
let detachPoints: (() => void) | null = null;
let detachScores: (() => void) | null = null;
let pushTimer: ReturnType<typeof setTimeout> | null = null;
let activeUid: string | null = null;
// Set while we write a remote value into the local wallet, so the resulting
// change notification doesn't bounce straight back up as a redundant write.
let applyingRemote = false;
// False until the server's copy has landed and been reconciled. Nothing is
// pushed up before that — see schedulePush.
let seeded = false;

function userDocRef(uid: string) {
  return doc(db!, "users", uid);
}

/** Debounced write-through of the current local state to Firestore. */
function schedulePush(uid: string): void {
  if (applyingRemote) return;
  if (!seeded) return;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    if (!db || activeUid !== uid) return;
    setDoc(
      userDocRef(uid),
      { points: getPoints(), othellosScores: getScores(), updatedAt: Date.now() },
      { merge: true },
    ).catch((e) => console.warn("[cloudPoints] push failed:", (e as Error)?.message || e));
  }, 800);
}

interface RemoteDoc {
  points?: unknown;
  othellosScores?: unknown;
}

/** Cloud wins for points; the record is the union of both so neither side
 *  loses it. On the seeding pass the balance earned while signed out is carried
 *  over on top of the cloud value, so playing before signing in doesn't cost the
 *  player anything (the welcome grant is excluded — see points.ts's UNSYNCED_KEY). */
function applyRemote(data: RemoteDoc | null, { seeding }: { seeding: boolean }): number[] {
  applyingRemote = true;
  try {
    if (data && typeof data.points === "number") {
      const carried = seeding ? getUnsyncedEarn() : 0;
      const next = data.points + carried;
      if (next !== getPoints()) setPoints(next);
    }
    const remoteScores = Array.isArray(data?.othellosScores) ? data.othellosScores : [];
    const merged = setScores([...getScores(), ...remoteScores]);
    return merged;
  } finally {
    applyingRemote = false;
  }
}

/**
 * Begin syncing for `uid`. Reconciles on the first server snapshot, then
 * mirrors changes both ways until detachCloudSync() is called. No-op if
 * Firestore isn't configured.
 */
export function attachCloudSync(uid: string): void {
  if (!db || !uid) return;
  detachCloudSync(); // drop any previous user's binding first
  activeUid = uid;
  seeded = false;
  // Stay "unsynced" until the reconcile lands, so anything earned in the gap
  // between signing in and the server answering is still carried over rather
  // than being wiped by the incoming cloud balance.
  setSyncActive(false);

  detachDoc = onSnapshot(
    userDocRef(uid),
    { includeMetadataChanges: true },
    (snap) => {
      if (activeUid !== uid) return; // a newer login/logout superseded us
      if (snap.metadata.hasPendingWrites) return;
      const data = (snap.exists() ? snap.data() : null) as RemoteDoc | null;

      if (!seeded) {
        const merged = applyRemote(data, { seeding: true });
        if (snap.metadata.fromCache) return;
        seeded = true;
        clearUnsyncedEarn();
        setSyncActive(true);
        // Push the reconciled state up. This also creates the document for an
        // account that has never synced, migrating the local wallet upward.
        setDoc(
          userDocRef(uid),
          { points: getPoints(), othellosScores: merged, updatedAt: Date.now() },
          { merge: true },
        ).catch((e) => console.warn("[cloudPoints] seed failed:", (e as Error)?.message || e));
        return;
      }

      applyRemote(data, { seeding: false });
    },
    (e) => console.warn("[cloudPoints] listen failed:", e?.message || e),
  );

  // Mirror subsequent local changes up to the cloud.
  detachPoints = onPointsChange(() => schedulePush(uid));
  detachScores = onScoresChange(() => schedulePush(uid));
}

/** Stop syncing (called on sign-out / user switch). Local wallet is untouched. */
export function detachCloudSync(): void {
  // Back to guest bookkeeping: earnings from here on are carried at next login.
  setSyncActive(false);
  seeded = false;
  if (detachDoc) { detachDoc(); detachDoc = null; }
  if (detachPoints) { detachPoints(); detachPoints = null; }
  if (detachScores) { detachScores(); detachScores = null; }
  if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
  activeUid = null;
}
